class IslanderAnnouncementController {
  constructor(root) {
    this.root = root;
    this.messages = Array.from(root.querySelectorAll('[data-announcement-message]'));
    this.controls = root.querySelector('[data-announcement-controls]');
    this.currentIndex = 0;
    this.userPaused = false;
    this.hovered = false;
    this.focused = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handlePreferenceChange = this.refresh.bind(this);
    this.handleMeasure = this.measure.bind(this);
    this.handleControls = this.onControls.bind(this);
    this.handlePause = event => {
      if (event.type === 'mouseenter') this.hovered = true;
      else this.focused = true;
      this.stop();
    };
    this.handleResume = event => {
      if (event.type === 'mouseleave') this.hovered = false;
      else this.focused = root.contains(event.relatedTarget);
      this.start();
    };
    this.handleVisibility = () => { if (document.hidden) this.stop(); else this.start(); };
    root.addEventListener('islander:delivery-display', this.handlePreferenceChange);
    root.addEventListener('click', this.handleControls);
    root.addEventListener('mouseenter', this.handlePause);
    root.addEventListener('mouseleave', this.handleResume);
    root.addEventListener('focusin', this.handlePause);
    root.addEventListener('focusout', this.handleResume);
    this.reducedMotion.addEventListener('change', this.handlePreferenceChange);
    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('resize', this.handleMeasure, {passive: true});
    if (typeof window.ResizeObserver === 'function') {
      this.resizeObserver = new window.ResizeObserver(this.handleMeasure);
      this.resizeObserver.observe(root);
    }
    this.refresh();
  }

  destroy() {
    this.stop();
    this.resizeObserver?.disconnect();
    this.root.removeEventListener('click', this.handleControls);
    this.root.removeEventListener('mouseenter', this.handlePause);
    this.root.removeEventListener('mouseleave', this.handleResume);
    this.root.removeEventListener('focusin', this.handlePause);
    this.root.removeEventListener('focusout', this.handleResume);
    this.root.removeEventListener('islander:delivery-display', this.handlePreferenceChange);
    this.reducedMotion.removeEventListener('change', this.handlePreferenceChange);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('resize', this.handleMeasure);
    document.documentElement.style.removeProperty('--islander-announcement-height');
  }

  get availableMessages() {
    return this.messages.filter(message => !message.hidden);
  }

  get shouldRotate() {
    return this.root.dataset.enableMobileRotation === 'true' && this.availableMessages.length > 1 &&
      !this.reducedMotion.matches && !this.userPaused && !this.hovered && !this.focused && !document.hidden;
  }

  measure() {
    const height = `${Math.ceil(this.root.getBoundingClientRect().height)}px`;
    const style = document.documentElement.style;
    if (style.getPropertyValue('--islander-announcement-height') !== height) style.setProperty('--islander-announcement-height', height);
  }

  refresh() {
    this.stop();
    const messages = this.availableMessages;
    const previous = messages.indexOf(this.messages.find(message => message.classList.contains('is-active')));
    this.currentIndex = previous >= 0 ? previous : 0;
    this.root.dataset.announcementReady = 'true';
    this.root.dataset.announcementCarousel = String(messages.length > 1);
    if (this.controls) this.controls.hidden = messages.length < 2;
    this.setActiveMessage(this.currentIndex);
    this.updatePauseControl();
    this.measure();
    this.start();
  }

  start() {
    if (!this.shouldRotate || this.rotationTimer) return;
    const delay = Number.parseInt(this.root.dataset.rotationDelay, 10) || 6000;
    this.rotationTimer = window.setInterval(() => this.setActiveMessage((this.currentIndex + 1) % this.availableMessages.length), delay);
  }

  stop() {
    if (this.rotationTimer) window.clearInterval(this.rotationTimer);
    this.rotationTimer = undefined;
  }

  updatePauseControl() {
    const button = this.controls?.querySelector('[data-announcement-action="pause"]');
    if (!button) return;
    button.setAttribute('aria-pressed', String(this.userPaused));
    button.setAttribute('aria-label', this.userPaused ? 'Play announcements' : 'Pause announcements');
    button.dataset.paused = String(this.userPaused);
    button.hidden = this.reducedMotion.matches || this.root.dataset.enableMobileRotation !== 'true';
  }

  onControls(event) {
    const button = event.target.closest('[data-announcement-action]');
    if (!button || !this.root.contains(button)) return;
    this.stop();
    if (button.dataset.announcementAction === 'pause') {
      this.userPaused = !this.userPaused;
      this.updatePauseControl();
    } else {
      const direction = button.dataset.announcementAction === 'previous' ? -1 : 1;
      this.setActiveMessage((this.currentIndex + direction + this.availableMessages.length) % this.availableMessages.length, true);
    }
    this.start();
  }

  setActiveMessage(index, announce = false) {
    const activeMessage = this.availableMessages[index];
    this.currentIndex = index;
    this.messages.forEach(message => {
      const active = message === activeMessage;
      message.classList.toggle('is-active', active);
      if (message.getAttribute('aria-hidden') !== String(!active)) message.setAttribute('aria-hidden', String(!active));
      if (message.inert !== !active) message.inert = !active;
    });
    if (announce) {
      const status = this.root.querySelector('[data-announcement-status]');
      if (status) status.textContent = activeMessage?.textContent.trim() || '';
    }
    this.root.dispatchEvent(new window.CustomEvent('islander:announcement-change'));
  }
}

function getDirectionalHeaderState(currentScrollY, lastScrollY, currentState = 'full', topThreshold = 4) {
  const current = Math.max(0, Number(currentScrollY) || 0);
  const previous = Math.max(0, Number(lastScrollY) || 0);
  const delta = current - previous;

  if (current <= topThreshold) return 'full';
  if (delta >= 4) return 'hidden';
  if (delta <= -4) return 'compact';
  return currentState;
}

class IslanderHeaderController {
  constructor(root) {
    this.root = root;
    this.previouslyFocused = null;
    this.openPanelElement = null;
    this.scrollFrame = null;
    this.lastScrollY = window.scrollY;
    this.headerState = this.lastScrollY <= 4 ? 'full' : 'compact';
    this.handleClick = this.onClick.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleScroll = this.onScroll.bind(this);
    this.handleResize = this.measure.bind(this);
    this.handleDocumentClick = this.onDocumentClick.bind(this);
    this.handleDesktopDisclosureToggle = this.onDesktopDisclosureToggle.bind(this);
    this.desktopDisclosures = Array.from(root.querySelectorAll('[data-desktop-navigation-disclosure]'));
    this.sectionWrapper = root.closest('.section-islander-header');

    root.addEventListener('click', this.handleClick);
    this.desktopDisclosures.forEach((disclosure) =>
      disclosure.addEventListener('toggle', this.handleDesktopDisclosureToggle),
    );
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleResize, { passive: true });
    this.syncStickyContainer();
    this.measure();

    if (root.dataset.stickyMode === 'condense') {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.onScroll();
    } else if (root.dataset.stickyMode === 'always') {
      root.classList.add('is-condensed');
    }
  }

  destroy() {
    this.closePanel({ restoreFocus: false });
    this.root.removeEventListener('click', this.handleClick);
    this.desktopDisclosures.forEach((disclosure) =>
      disclosure.removeEventListener('toggle', this.handleDesktopDisclosureToggle),
    );
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
    this.sectionWrapper?.classList.remove('islander-header-section--sticky');
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
  }

  refresh() {
    this.syncStickyContainer();
    this.measure();
    this.onScroll();
  }

  syncStickyContainer() {
    this.sectionWrapper?.classList.toggle(
      'islander-header-section--sticky',
      this.root.dataset.stickyMode !== 'none',
    );
  }

  measure() {
    this.condenseThreshold = this.root.getBoundingClientRect().height;
  }

  onScroll() {
    if (this.root.dataset.stickyMode !== 'condense' || this.scrollFrame) return;
    this.scrollFrame = window.requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;
      const state = getDirectionalHeaderState(currentScrollY, this.lastScrollY, this.headerState);

      if (state === 'hidden' && this.headerState !== 'hidden') this.closeDesktopDisclosures();
      this.root.classList.toggle('is-condensed', state === 'compact' || state === 'hidden');
      this.root.classList.toggle('is-hidden', state === 'hidden');
      if (state === 'full') this.root.classList.remove('is-hidden', 'is-condensed');
      this.headerState = state;
      this.lastScrollY = currentScrollY;
      this.scrollFrame = null;
    });
  }

  onClick(event) {
    const menuToggle = event.target.closest('[data-menu-toggle]');
    if (menuToggle) {
      if (this.openPanelElement?.dataset.headerPanel === 'menu') {
        this.closePanel();
        return;
      }
      this.openPanel('menu', menuToggle);
      return;
    }

    const searchToggle = event.target.closest('[data-search-toggle]');
    if (searchToggle) {
      this.openPanel('search', searchToggle);
      return;
    }

    const drawerBack = event.target.closest('[data-drawer-back]');
    if (drawerBack) {
      event.preventDefault();
      this.closeDrawerLevel(drawerBack);
      return;
    }

    const drawerSummary = event.target.closest('[data-drawer-disclosure] > summary');
    if (drawerSummary) {
      window.requestAnimationFrame(() => {
        const backButton = drawerSummary.parentElement?.querySelector(
          ':scope > .islander-drawer__submenu-panel [data-drawer-back]',
        );
        backButton?.focus({ preventScroll: true });
      });
      return;
    }

    if (event.target.closest('[data-drawer-close]')) this.closePanel();
  }

  closeDrawerLevel(backButton) {
    const disclosure = backButton.closest('details');
    if (!disclosure) return;
    disclosure.open = false;
    disclosure.querySelector(':scope > summary')?.focus({ preventScroll: true });
  }

  onDesktopDisclosureToggle(event) {
    if (event.currentTarget.open) this.closeDesktopDisclosures(event.currentTarget);
  }

  onDocumentClick(event) {
    if (!this.root.contains(event.target)) this.closeDesktopDisclosures();
  }

  closeDesktopDisclosures(exception = null) {
    this.desktopDisclosures.forEach((disclosure) => {
      if (disclosure !== exception && disclosure.open) disclosure.open = false;
    });
  }

  openPanel(type, opener) {
    const panel = this.root.querySelector(`[data-header-panel="${type}"]`);
    if (!panel) return;
    if (this.openPanelElement) this.closePanel({ restoreFocus: false });
    this.closeDesktopDisclosures();

    this.previouslyFocused = opener;
    this.openPanelElement = panel;
    const headerBottom = this.root.querySelector('.islander-header__surface')?.getBoundingClientRect().bottom ?? 0;
    panel.style?.setProperty('--islander-drawer-top', `${Math.max(0, Math.round(headerBottom))}px`);
    panel.hidden = false;
    document.body.classList.add('islander-panel-open');
    this.setToggleState(type, true);

    const initialFocus = panel.querySelector(
      type === 'search' ? 'input[type="search"]' : '.islander-drawer__menu-row',
    );
    window.requestAnimationFrame(() => initialFocus?.focus({ preventScroll: true }));
  }

  closePanel({ restoreFocus = true } = {}) {
    if (!this.openPanelElement) return;
    const type = this.openPanelElement.dataset.headerPanel;
    this.openPanelElement.querySelectorAll?.('[data-drawer-disclosure][open]').forEach((disclosure) => {
      disclosure.open = false;
    });
    this.openPanelElement.hidden = true;
    this.openPanelElement = null;
    document.body.classList.remove('islander-panel-open');
    this.setToggleState(type, false);

    if (restoreFocus && this.previouslyFocused?.isConnected) {
      this.previouslyFocused.focus({ preventScroll: true });
    }
    this.previouslyFocused = null;
  }

  setToggleState(type, isOpen) {
    const selector = type === 'menu' ? '[data-menu-toggle]' : '[data-search-toggle]';
    this.root.querySelectorAll(selector).forEach((toggle) => {
      toggle.setAttribute('aria-expanded', String(isOpen));
      if (type === 'menu') toggle.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
    });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      if (this.openPanelElement) {
        event.preventDefault();
        this.closePanel();
        return;
      }

      const openDisclosure = this.desktopDisclosures.find((disclosure) => disclosure.open);
      if (openDisclosure) {
        event.preventDefault();
        openDisclosure.open = false;
        openDisclosure.querySelector('summary')?.focus({ preventScroll: true });
        return;
      }
    }

    if (!this.openPanelElement) return;
    if (event.key === 'Tab') this.trapFocus(event);
  }

  trapFocus(event) {
    const focusable = Array.from(
      this.openPanelElement.querySelectorAll(
        'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), details > summary',
      ),
    ).filter((element) => !element.hidden && element.getClientRects().length > 0);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

const controllerKey = 'islanderController';

function initialiseController(root, Controller) {
  if (!root || root[controllerKey]) return root?.[controllerKey];
  const controller = new Controller(root);
  root[controllerKey] = controller;
  return controller;
}

function initialiseScope(scope = document) {
  if (scope.matches?.('islander-header')) initialiseController(scope, IslanderHeaderController);
  if (scope.matches?.('islander-announcement')) initialiseController(scope, IslanderAnnouncementController);
  scope.querySelectorAll?.('islander-header').forEach((root) => initialiseController(root, IslanderHeaderController));
  scope
    .querySelectorAll?.('islander-announcement')
    .forEach((root) => initialiseController(root, IslanderAnnouncementController));
}

function destroyScope(scope) {
  const roots = [];
  if (scope.matches?.('islander-header, islander-announcement')) roots.push(scope);
  roots.push(...(scope.querySelectorAll?.('islander-header, islander-announcement') ?? []));
  roots.forEach((root) => {
    root[controllerKey]?.destroy();
    delete root[controllerKey];
  });
}

window.IslanderHeaderController = IslanderHeaderController;
initialiseScope();

document.addEventListener('shopify:section:load', (event) => initialiseScope(event.target));
document.addEventListener('shopify:section:unload', (event) => destroyScope(event.target));
